import React, { useEffect, useState } from "react";

import DocumentPreviewModal from "../components/DocumentPreviewModal";
import { fetchPlatformSettings } from "../services/adminService";
import {
  getAdminDepositScreenshotSignedUrl,
  listAdminDeposits,
  listAdminWithdrawals,
  reviewAdminDeposit,
  reviewAdminWithdrawal,
} from "../services/paymentService";

function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (data?.results && Array.isArray(data.results)) return data.results;
  return [];
}

export default function AdminPaymentReviewPage() {
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [message, setMessage] = useState("");
  const [platform, setPlatform] = useState(null);
  const [depositApprove, setDepositApprove] = useState({
    open: false,
    id: "",
    amountLabel: "",
    remark: "",
    busy: false,
  });
  const [depositReject, setDepositReject] = useState({
    open: false,
    id: "",
    reason: "",
    busy: false,
  });
  const [docPreview, setDocPreview] = useState({
    open: false,
    loading: false,
    url: "",
    error: "",
    title: "Deposit proof",
    subtitle: "",
    authRequired: false,
    contentHint: "",
  });

  const load = async () => {
    try {
      const [depositRes, withdrawalRes, platformRes] = await Promise.allSettled([
        listAdminDeposits(),
        listAdminWithdrawals(),
        fetchPlatformSettings(),
      ]);
      if (depositRes.status === "fulfilled") {
        setDeposits(unwrapList(depositRes.value.data));
      } else {
        setDeposits([]);
      }
      if (withdrawalRes.status === "fulfilled") {
        setWithdrawals(unwrapList(withdrawalRes.value.data));
      } else {
        setWithdrawals([]);
      }
      if (platformRes.status === "fulfilled") {
        setPlatform(platformRes.value.data);
      }
      const failed = [depositRes, withdrawalRes].find((r) => r.status === "rejected");
      if (failed) {
        setMessage(failed.reason?.response?.data?.detail || "Failed to load admin payment data.");
      } else {
        setMessage("");
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to load admin payment data.");
      setDeposits([]);
      setWithdrawals([]);
    }
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const remarkRequired = Boolean(platform?.deposit_approval_remark_required);

  const closeDocPreview = () => {
    setDocPreview({
      open: false,
      loading: false,
      url: "",
      error: "",
      title: "Deposit proof",
      subtitle: "",
      authRequired: false,
      contentHint: "",
    });
  };

  const openDepositProof = async (d) => {
    if (!d?.id) return;
    setMessage("");
    setDocPreview({
      open: true,
      loading: true,
      url: "",
      error: "",
      title: "Deposit proof",
      subtitle: [d.user_name || d.user_email, d.bank_channel, d.transaction_ref, d.amount != null ? `BDT ${d.amount}` : ""]
        .filter(Boolean)
        .join(" · "),
      authRequired: false,
      contentHint: "",
    });
    try {
      const { data } = await getAdminDepositScreenshotSignedUrl(d.id);
      const url = data?.url || "";
      if (!url) {
        setDocPreview((prev) => ({
          ...prev,
          loading: false,
          error: "No document URL returned for this proof.",
        }));
        setMessage("No signed URL returned for this proof.");
        return;
      }
      setDocPreview((prev) => ({
        ...prev,
        loading: false,
        url,
        error: "",
        authRequired: Boolean(data?.auth_required),
        contentHint: data?.content_hint || "",
      }));
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not open proof.";
      setDocPreview((prev) => ({ ...prev, loading: false, url: "", error: msg }));
      setMessage(msg);
    }
  };

  const openApproveDeposit = (d) => {
    setMessage("");
    setDepositApprove({
      open: true,
      id: d.id,
      amountLabel: `BDT ${d.amount}`,
      remark: "",
      busy: false,
    });
  };

  const submitApproveDeposit = async () => {
    const remark = depositApprove.remark.trim();
    if (remarkRequired && !remark) {
      setMessage("Admin remark is required when approving deposits (platform setting).");
      return;
    }
    if (remark.length > 1000) {
      setMessage("Admin remark cannot exceed 1000 characters.");
      return;
    }
    setDepositApprove((s) => ({ ...s, busy: true }));
    setMessage("");
    try {
      await reviewAdminDeposit(depositApprove.id, { status: "APPROVED", admin_remark: remark });
      setDepositApprove({ open: false, id: "", amountLabel: "", remark: "", busy: false });
      await load();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to approve deposit.");
      setDepositApprove((s) => ({ ...s, busy: false }));
    }
  };

  const openRejectDeposit = (d) => {
    setMessage("");
    setDepositReject({ open: true, id: d.id, reason: "", busy: false });
  };

  const submitRejectDeposit = async () => {
    const reason = depositReject.reason.trim();
    if (!reason) {
      setMessage("Rejection reason is required.");
      return;
    }
    setDepositReject((s) => ({ ...s, busy: true }));
    setMessage("");
    try {
      await reviewAdminDeposit(depositReject.id, { status: "REJECTED", rejection_reason: reason });
      setDepositReject({ open: false, id: "", reason: "", busy: false });
      await load();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to reject deposit.");
      setDepositReject((s) => ({ ...s, busy: false }));
    }
  };

  const handleWithdrawalReview = async (id, status) => {
    try {
      await reviewAdminWithdrawal(id, { status });
      await load();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to review withdrawal.");
    }
  };

  return (
    <div className="page">
      <h2>Admin Payment Reviews</h2>
      {message && <p className="msg">{message}</p>}
      <div className="grid-2">
        <div className="card" style={{ padding: 14 }}>
          <h3>Deposit Requests</h3>
          {remarkRequired ? (
            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
              Platform setting: <strong>admin remark required</strong> on each approval.
            </p>
          ) : null}
          <div className="table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Channel / Ref</th>
                  <th>Status</th>
                  <th>Doc</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {deposits.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="muted">
                      No deposit requests.
                    </td>
                  </tr>
                ) : (
                  deposits.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <div>{d.user_name || "—"}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {d.user_email || ""}
                        </div>
                      </td>
                      <td>BDT {d.amount}</td>
                      <td>
                        <div>{d.bank_channel || "—"}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {d.transaction_ref || ""}
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${d.status}`}>{d.status}</span>
                      </td>
                      <td>
                        {d.screenshot_url ? (
                          <button type="button" className="btn-secondary" onClick={() => openDepositProof(d)}>
                            Doc View
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {d.status === "PENDING" ? (
                          <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button type="button" onClick={() => openApproveDeposit(d)}>
                              Approve
                            </button>
                            <button type="button" className="btn-danger" onClick={() => openRejectDeposit(d)}>
                              Reject
                            </button>
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card" style={{ padding: 14 }}>
          <h3>Withdrawal Requests</h3>
          <ul className="list">
            {withdrawals.map((w) => (
              <li key={w.id} className="list-item">
                <span>
                  BDT {w.amount}
                  {Number(w.fee_bdt) > 0 ? (
                    <span className="muted" style={{ fontSize: 12 }}>
                      {" "}
                      (fee BDT {w.fee_bdt})
                    </span>
                  ) : null}{" "}
                  <span className={`badge badge-${w.status}`}>{w.status}</span>
                </span>
                {w.status === "PENDING" && (
                  <>
                    <button type="button" onClick={() => handleWithdrawalReview(w.id, "PROCESSING")}>
                      Process
                    </button>
                    <button type="button" className="btn-danger" onClick={() => handleWithdrawalReview(w.id, "REJECTED")}>
                      Reject
                    </button>
                  </>
                )}
                {w.status === "PROCESSING" && (
                  <>
                    <button type="button" onClick={() => handleWithdrawalReview(w.id, "COMPLETED")}>
                      Complete
                    </button>
                    <button type="button" className="btn-danger" onClick={() => handleWithdrawalReview(w.id, "FAILED")}>
                      Fail
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <DocumentPreviewModal
        open={docPreview.open}
        url={docPreview.url}
        title={docPreview.title}
        subtitle={docPreview.subtitle}
        loading={docPreview.loading}
        error={docPreview.error}
        authRequired={docPreview.authRequired}
        contentHint={docPreview.contentHint}
        onClose={closeDocPreview}
      />

      {depositApprove.open && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Approve deposit</h3>
            <p className="modal-sub">
              Confirm approval for <strong>{depositApprove.amountLabel}</strong>.
            </p>
            <label className="muted" style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
              Admin remark
              {remarkRequired ? <span> (required)</span> : <span className="muted"> (optional)</span>}
            </label>
            <textarea
              rows={4}
              maxLength={1000}
              value={depositApprove.remark}
              onChange={(e) => setDepositApprove({ ...depositApprove, remark: e.target.value })}
              placeholder="Enter approval remarks, payment verification notes, or additional instructions..."
              disabled={depositApprove.busy}
            />
            <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>
              {depositApprove.remark.trim().length}/1000 characters
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button
                type="button"
                className="btn-secondary"
                disabled={depositApprove.busy}
                onClick={() =>
                  setDepositApprove({ open: false, id: "", amountLabel: "", remark: "", busy: false })
                }
              >
                Cancel
              </button>
              <button type="button" disabled={depositApprove.busy} onClick={submitApproveDeposit}>
                {depositApprove.busy ? "Submitting…" : "Confirm approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {depositReject.open && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Reject deposit</h3>
            <p className="modal-sub">Provide a reason for audit and investor visibility.</p>
            <textarea
              value={depositReject.reason}
              onChange={(e) => setDepositReject({ ...depositReject, reason: e.target.value })}
              placeholder="Enter rejection reason"
              disabled={depositReject.busy}
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button
                type="button"
                className="btn-secondary"
                disabled={depositReject.busy}
                onClick={() => setDepositReject({ open: false, id: "", reason: "", busy: false })}
              >
                Cancel
              </button>
              <button type="button" className="btn-danger" disabled={depositReject.busy} onClick={submitRejectDeposit}>
                {depositReject.busy ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
